import React, { useEffect, useState } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from "@mui/material";
import {
    makePOSTRequestOnUserService,
    makePUTRequestOnUserService,
    makeDELETERequestOnUserServiceWithQueryParameter,
} from "../../Api/api";

export const AddressProof = ({ poa, fetchKycDocs, currentUser, user_type, setValue, getErrorMessage, adminName, accountTypeState }: any) => {
    const toastr = require("toastr");

    const TYPE_MAP: any = {
        aadhar: "aadhar_card",
        driver_license: "driver_license",
        passport: "passport"
    };

    const REVERSE_TYPE_MAP: any = {
        aadhar_card: "aadhar",
        driver_license: "driver_license",
        passport: "passport"
    };

    const ADDRESS_TYPE_CHOICES = [
        { value: "residential", label: "Residential" },
        { value: "business", label: "Business" },
        { value: "residential_cum_business", label: "Residential cum Business" },
    ];

    const PASSPORT_TYPE_CHOICES = [
        { value: "indian", label: "Indian Passport" },
        { value: "foreign", label: "Foreign Passport" },
    ];

    const TYPES_WITH_ADDRESS_TYPE = ["aadhar", "driver_license", "passport"];

    const isNRI = accountTypeState === "individual_non_resident";

    const [type, setType] = useState<string>(isNRI ? "passport" : "aadhar");
    const [file, setFile] = useState<any>(null);
    const [addressType, setAddressType] = useState("");
    const [passportType, setPassportType] = useState("");

    const [form, setForm] = useState({
        uuid: "",
        address: "",
        expiry_date: "",
        district_or_city: "",
        state: "",
        pincode: "",
        country: "",
        password: ""
    });

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [loading, setLoading] = useState(false);

    const formatDate = (date: string) => {
        if (!date) return "";
        return date.includes("T") ? date.split("T")[0] : date;
    };

    useEffect(() => {
        if (poa && Object.keys(poa).length > 0) {
            const formattedDate =
                poa?.expiry_date && poa?.type !== "aadhar_card"
                    ? formatDate(poa.expiry_date)
                    : "";
            setForm({
                uuid: poa?.uuid || "",
                address: poa?.address || "",
                expiry_date: formattedDate,
                district_or_city: poa?.district_or_city || "",
                state: poa?.state || "",
                pincode: poa?.pincode || "",
                country: poa?.country || "",
                password: poa?.kyc_doc_plaintext_password || "",
            });
            setType(REVERSE_TYPE_MAP[poa?.type] || (isNRI ? "passport" : "aadhar"));
            setAddressType(poa?.address_type || "");
            if (poa?.type === "passport" && poa?.passport_type) {
                setPassportType(poa.passport_type);
            }
        }
    }, [poa]);

    const handleChange = (e: any) => {
        setForm((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const getEndpoints = () => {
        const apiType = TYPE_MAP[type];
        return {
            create: `/partneruser/kyc/${apiType}/create/`,
            update: `/partneruser/kyc/${apiType}/update/`,
            delete: `/partneruser/kyc/${apiType}/delete/${currentUser.uuid}/?user_type=${user_type}`,
        };
    };

    const handleSubmit = async () => {
        if (TYPES_WITH_ADDRESS_TYPE.includes(type) && !addressType) {
            toastr.error("Address Type is required");
            return;
        }

        if (type === "passport" && !passportType) {
            toastr.error("Passport Type is required");
            return;
        }

        try {
            setLoading(true);

            const fd = new FormData();

            fd.append("uuid", form.uuid);
            fd.append("address", form.address);
            fd.append("user_id", currentUser.uuid);
            fd.append("user_type", user_type);
            fd.append("kyc_doc_plaintext_password", form.password || "");
            fd.append("country", form.country || "");
            fd.append("admin_name", adminName || "");

            if (form.district_or_city)
                fd.append("district_or_city", form.district_or_city);
            if (form.state) fd.append("state", form.state);
            if (form.pincode) fd.append("pincode", form.pincode);
            if (type !== "aadhar" && form.expiry_date) {
                fd.append("expiry_date", form.expiry_date);
            }
            if (TYPES_WITH_ADDRESS_TYPE.includes(type)) {
                fd.append("address_type", addressType);
            }
            if (type === "passport") {
                fd.append("passport_type", passportType);
            }

            if (file) {
                fd.append("front_part", file, `${Date.now()}_${file.name}`);
            }

            const { create, update } = getEndpoints();

            let resp;

            if (poa?.uuid) {
                resp = await makePUTRequestOnUserService(update, fd);
            } else {
                resp = await makePOSTRequestOnUserService(create, fd);
            }

            if (resp.status === 200 || resp.status === 201) {
                setFile(null);
                await fetchKycDocs();
                toastr.success("Saved successfully");
                setValue((prev: number) => prev + 1);
            }
        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);

            const { delete: deleteUrl } = getEndpoints();

            const resp = await makeDELETERequestOnUserServiceWithQueryParameter(deleteUrl);

            if (resp.status === 204) {
                setOpenDeleteDialog(false);
                await fetchKycDocs();
                toastr.success("Deleted successfully");
            }

        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleTypeChange = (newType: string) => {
        setType(newType);
        if (newType !== "passport") {
            setPassportType("");
        }
        if (!TYPES_WITH_ADDRESS_TYPE.includes(newType)) {
            setAddressType("");
        }
    };

    return (
        <Grid container spacing={4}>
            <Grid item xs={5}>
                <Box
                    sx={{
                        height: 350,
                        border: "1px dashed #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        background: "#fafafa",
                        overflow: "hidden"
                    }}
                >
                    {file ? (
                        file.type === "application/pdf" ? (
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    const url = URL.createObjectURL(file);
                                    window.open(url, "_blank");
                                }}
                            >
                                View PDF
                            </Button>
                        ) : (
                            <img
                                src={URL.createObjectURL(file)}
                                alt="preview"
                                style={{ maxWidth: "100%", maxHeight: "100%" }}
                            />
                        )
                    ) :
                        poa?.front_image ? (
                            poa?.front_part_image_extension === "pdf" ? (
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        const byteCharacters = atob(poa.front_image);
                                        const byteNumbers = new Array(byteCharacters.length)
                                            .fill(0)
                                            .map((_, i) => byteCharacters.charCodeAt(i));
                                        const blob = new Blob(
                                            [new Uint8Array(byteNumbers)],
                                            { type: "application/pdf" }
                                        );
                                        const url = URL.createObjectURL(blob);
                                        window.open(url, "_blank");
                                    }}
                                >
                                    View PDF
                                </Button>
                            ) : (
                                <img
                                    src={`data:image/${poa.front_part_image_extension};base64,${poa.front_image}`}
                                    alt="Document"
                                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                                />
                            )
                        ) : (
                            <span>Upload Document</span>
                        )}
                </Box>

                <input
                    style={{ marginTop: "10px" }}
                    type="file"
                    onChange={(e) => setFile(e.target.files?.[0])}
                />
            </Grid>

            <Grid item xs={7}>
                <Paper sx={{ p: 3 }}>
                    <h2>Address Proof</h2>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="document-type-label">Document Type</InputLabel>

                        <Select
                            labelId="document-type-label"
                            id="document-type"
                            value={type}
                            label="Document Type"
                            onChange={(e) => handleTypeChange(e.target.value as string)}
                            disabled={isNRI}
                        >
                            <MenuItem value="aadhar">Aadhaar</MenuItem>
                            <MenuItem value="driver_license">Driver License</MenuItem>
                            <MenuItem value="passport">Passport</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Document Number"
                        name="uuid"
                        value={form.uuid}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    {TYPES_WITH_ADDRESS_TYPE.includes(type) && (
                        <FormControl fullWidth sx={{ mb: 2 }} required>
                            <InputLabel>Address Type</InputLabel>
                            <Select
                                value={addressType}
                                onChange={(e) => setAddressType(e.target.value)}
                                label="Address Type"
                            >
                                {ADDRESS_TYPE_CHOICES.map((choice) => (
                                    <MenuItem key={choice.value} value={choice.value}>
                                        {choice.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {type === "passport" && (
                        <FormControl fullWidth sx={{ mb: 2 }} required>
                            <InputLabel>Passport Type</InputLabel>
                            <Select
                                value={passportType}
                                onChange={(e) => setPassportType(e.target.value)}
                                label="Passport Type"
                            >
                                {PASSPORT_TYPE_CHOICES.map((choice) => (
                                    <MenuItem key={choice.value} value={choice.value}>
                                        {choice.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <TextField
                        fullWidth
                        label="Address"
                        name="address"
                        value={form.address}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    {type !== "aadhar" && (
                        <TextField
                            fullWidth
                            type="date"
                            name="expiry_date"
                            label="Expiry Date"
                            value={form.expiry_date}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2 }}
                        />
                    )}

                    <TextField
                        fullWidth
                        label="City"
                        name="district_or_city"
                        value={form.district_or_city}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="State"
                        name="state"
                        value={form.state}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Pincode"
                        name="pincode"
                        value={form.pincode}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Country"
                        name="country"
                        value={form.country}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {poa?.uuid ? "Update" : "Create"}
                        </Button>

                        {poa?.uuid && (
                            <Button
                                variant="outlined"
                                onClick={() => setOpenDeleteDialog(true)}
                            >
                                Delete
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Grid>

            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Delete Document</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this document?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

        </Grid>
    );
};